'use strict';

import './radialMenu.css';
import './radialMenu.theme.dark.css';
import './radialMenu.theme.light.css';
import backButtonImg from '../../assets/backButton2.png';

var DEFAULT_SIZE = 100;
var MIN_SECTORS = 3;

// Dark theme keeps the original painting model: each sector is a single
// translucent fill color, toggled between these three states as SVG
// presentation attributes.
var THEME_TOKENS = {
  dark: {
    baseFill: '#5f87b750',
    selectedFill: '#5f87b785',
    dummyFill: '#3C506842',
  },
};

// Light theme instead paints an outline: transparent fill, colored stroke,
// colored label — one solid color per category, resolved by title. Visual
// on/off (hover, selected) is handled by radialMenu.theme.light.css via
// fill-opacity, not by changing this color.
var LIGHT_SECTOR_COLORS = {
  Analytik: '#F09349',
  Beratung: '#6D4D61',
  Change: '#BE4746',
};
var LIGHT_DEFAULT_SECTOR_COLOR = '#8F8577';

// Shared with React components (e.g. the widget card) that need the same
// per-category color the wheel itself uses in light theme.
export function getLightSectorColor(title) {
  return LIGHT_SECTOR_COLORS[title] || LIGHT_DEFAULT_SECTOR_COLOR;
}

// Resolves the color a sector would have in the wheel given its full
// breadcrumb (root → leaf titles): Analytik/Beratung/Change reset to their
// own color at whatever depth they appear, everything else inherits the
// nearest such ancestor's color — or the neutral default if there isn't
// one. Mirrors RadialMenu.prototype.getSectorColor/showNestedMenu, for
// callers (e.g. the widget card) that only have the breadcrumb, not a
// live RadialMenu instance.
export function resolveBreadcrumbColor(titles) {
  var color = LIGHT_DEFAULT_SECTOR_COLOR;
  for (var i = 0; i < titles.length; i += 1) {
    if (LIGHT_SECTOR_COLORS[titles[i]]) {
      color = LIGHT_SECTOR_COLORS[titles[i]];
    }
  }
  return color;
}

// source code: https://github.com/axln/radial-menu-js
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function RadialMenu(params) {
  var self = this;

  self.parent = params.parent || [];
  self.updateBreadcrumb = params.updateBreadcrumb || null;

  self.size = params.size || DEFAULT_SIZE;
  self.onClick = params.onClick || null;
  self.handleDoubleClick = params.handleDoubleClick || null;
  self.onClose = params.onClose || null;
  self.menuItems = params.menuItems
    ? params.menuItems
    : [
        { id: 'one', title: 'One' },
        { id: 'two', title: 'Two' },
      ];

  self.theme = params.theme === 'light' ? 'light' : 'dark';
  self.tokens = THEME_TOKENS[self.theme] || null;

  self.radius = 45;
  self.depth = 0;
  self.innerRadius = self.radius * 0.4;
  self.sectorSpace = self.radius * 0.06;
  self.sectorCount = Math.max(self.menuItems.length, MIN_SECTORS);
  self.closeOnClick =
    params.closeOnClick !== undefined ? !!params.closeOnClick : false;

  self.scale = 1;
  self.holder = null;
  self.parentMenu = [];
  self.parentItems = [];
  self.levelItems = null;

  // Light theme only: once the user drills into a category (e.g. Analytik),
  // every descendant sector reuses that category's color instead of being
  // resolved by its own title — unless a descendant is itself named
  // Analytik/Beratung/Change, in which case it resets to its own color
  // (see getSectorColor/showNestedMenu). activeColorStack mirrors
  // parentMenu/parentItems so returnToParentMenu can restore the exact
  // color that was active at each level, at any depth.
  self.activeColor = null;
  self.activeColorStack = [];

  self.openWidget = false;

  self.createHolder();

  self.currentMenu = null;
  document.addEventListener('wheel', self.onMouseWheel.bind(self));
  document.addEventListener('keydown', self.onKeyDown.bind(self));
}

RadialMenu.prototype.getSectorColor = function (title) {
  var self = this;
  // Analytik/Beratung/Change always get their own designated color,
  // regardless of where they sit in the hierarchy — only sectors that
  // are NOT one of these three fall back to the inherited/default color.
  if (LIGHT_SECTOR_COLORS[title]) {
    return LIGHT_SECTOR_COLORS[title];
  }
  if (self.activeColor) {
    return self.activeColor;
  }
  return LIGHT_DEFAULT_SECTOR_COLOR;
};

RadialMenu.prototype.executePath = function (path) {
  var self = this;
  self.updateBreadcrumb({}, true);
  for (const p of path) {
    var allg = Array.from(document.getElementsByTagName('g'));

    const g = allg.filter(g => g.getAttribute('data-id') === String(p))[0];

    // Stale paths (e.g. saved for another usergroup or a removed widget)
    // reference ids that no longer exist; each step depends on the previous
    // one, so stop replaying instead of dispatching on undefined.
    if (!g) return;

    g.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.open = function (path) {
  var self = this;
  if (!self.currentMenu) {
    self.currentMenu = self.createMenu('menu inner', self.menuItems);
    self.holder.appendChild(self.currentMenu);

    // wait DOM commands to apply and then set class to allow transition to take effect
    RadialMenu.nextTick(function () {
      self.currentMenu.setAttribute('class', 'menu');
    });

    if (path.length > 0) {
      self.executePath(path);
    }
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.getParentMenu = function () {
  var self = this;
  if (self.parentMenu.length > 0) {
    return self.parentMenu[self.parentMenu.length - 1];
  } else {
    return null;
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.createHolder = function () {
  var self = this;

  self.holder = document.createElement('div');
  self.holder.className = 'menuHolder';
  self.holder.setAttribute('data-wheel-theme', self.theme);
  self.holder.style.width = '100%';
  self.holder.style.height = '100%';
  self.holder.style.display = 'flex';
  self.holder.style.alignItems = 'center';

  self.parent.appendChild(self.holder);
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.showNestedMenu = function (item) {
  var self = this;
  self.parentMenu.push(self.currentMenu);
  self.parentItems.push(self.levelItems);
  if (self.theme === 'light') {
    self.activeColorStack.push(self.activeColor);
    // Entering Analytik/Beratung/Change resets to its own color even
    // if we're already nested under an unrelated (grey) ancestor; entering
    // anything else just keeps propagating whatever color is active.
    self.activeColor = self.getSectorColor(item.title);
  }
  self.currentMenu = self.createMenu('menu inner', item.items, true);
  self.holder.appendChild(self.currentMenu);

  // wait DOM commands to apply and then set class to allow transition to take effect
  RadialMenu.nextTick(function () {
    self.getParentMenu().setAttribute('class', 'menu outer');
    self.currentMenu.setAttribute('class', 'menu');
  });
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.returnToParentMenu = function () {
  var self = this;

  self.depth -= 1;
  self.updateBreadcrumb({ depth: self.depth });

  var parent = Array.from(self.getParentMenu().childNodes).find(
    el => el.getAttribute('class') === 'sector selected',
  );
  parent.setAttribute('open', false);
  if (self.theme === 'light') {
    var parentColor = self.getSectorColor(parent.getAttribute('title'));
    parent.setAttribute('fill', parentColor);
    parent.setAttribute('stroke', parentColor);
  } else {
    parent.setAttribute('fill', self.tokens.baseFill);
  }
  self.getParentMenu().setAttribute('class', 'menu');
  RadialMenu.setClassAndWaitForTransition(self.currentMenu, 'menu inner').then(
    function () {
      self.currentMenu.remove();
      self.currentMenu = self.parentMenu.pop();
      self.levelItems = self.parentItems.pop();
      if (self.theme === 'light') {
        self.activeColor = self.activeColorStack.pop() || null;
      }
    },
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.handleClick = function (isDoubleClick) {
  var self = this;

  var selectedIndex = self.getSelectedIndex();
  if (selectedIndex >= 0) {
    var item = self.levelItems[selectedIndex];
    if (item.items) {
      self.showNestedMenu(item);
    } else {
      if (self.handleDoubleClick && isDoubleClick) {
        self.handleDoubleClick(item);
      } else if (self.onClick) {
        self.onClick(item);
      }
    }
  }
};

RadialMenu.prototype.handleClose = function () {
  var self = this;

  var selectedIndex = self.getSelectedIndex();
  if (selectedIndex >= 0) {
    var item = self.levelItems[selectedIndex];
    if (item.items) {
      self.onClose();
      // self.showNestedMenu(item);
    } else {
      if (self.onClose) {
        self.onClose(item);
      }
    }
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.handleCenterClick = function () {
  var self = this;

  if (self.parentItems.length > 0) {
    self.returnToParentMenu();
    self.handleClose();
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.createCenter = function (svg, title, centerImg, size) {
  var self = this;
  size = size || 8;
  var g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  g.setAttribute('class', 'center');

  var centerCircle = self.createCircle(
    0,
    0,
    self.innerRadius - self.sectorSpace / 3,
  );

  var centerInnerCircle = null;
  if (self.theme === 'light') {
    centerInnerCircle = self.createCircle(
      0,
      0,
      (self.innerRadius - self.sectorSpace / 3) * 0.62,
    );
    centerInnerCircle.setAttribute('class', 'hub-inner');
  }

  if (self.theme !== 'light') {
    g.addEventListener('mouseover', function (event) {
      var brothers = Array.from(svg.childNodes);
      var openedBrothers = brothers.filter(
        g => g.getAttribute('open') === 'true',
      );

      if (openedBrothers.length > 0) {
        var img = g.querySelector('image');
        if (!img) return;

        var originalHref = img.getAttribute('href');
        g.setAttribute('data-original-href', originalHref);

        img.setAttribute('href', backButtonImg);
        img.setAttribute('height', '25');
        img.setAttribute('width', '25');
        img.setAttribute('x', 0);
        img.setAttribute('y', 0);
        img.setAttribute('transform', 'translate(-12.5,-12.5)');
      }
    });

    g.addEventListener('mouseout', function () {
      var brothers = Array.from(svg.childNodes);
      var openedBrothers = brothers.filter(
        g => g.getAttribute('open') === 'true',
      );

      if (openedBrothers.length > 0) {
        var img = g.querySelector('image');
        if (!img) return;

        // Recupera o href original salvo no mouseover
        var originalHref = g.getAttribute('data-original-href');
        if (originalHref) {
          img.setAttribute('href', originalHref);
          img.setAttribute('height', '30');
          img.setAttribute('width', '30');
          img.setAttribute('x', 0);
          img.setAttribute('y', 0);
          img.setAttribute('transform', 'translate(-15,-15)');
        }
      }
    });
  }

  g.appendChild(centerCircle);
  if (centerInnerCircle) {
    g.appendChild(centerInnerCircle);
  }

  // text in the center circle
  // var text = self.createText(0,0, title);
  // g.appendChild(text);

  if (self.theme === 'light') {
    // Reference model: a static vector chevron instead of the raster
    // back-button image, always visible (no hover/nesting swap needed).
    var hubInnerR = (self.innerRadius - self.sectorSpace / 3) * 0.62;
    var chevronDx = hubInnerR * 0.129;
    var chevronDy = hubInnerR * 0.258;
    var chevron = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );
    chevron.setAttribute('class', 'hub-chevron');
    chevron.setAttribute(
      'd',
      `M${RadialMenu.numberToString(chevronDx)} ${RadialMenu.numberToString(-chevronDy)} ` +
        `${RadialMenu.numberToString(-chevronDx)} 0 ` +
        `${RadialMenu.numberToString(chevronDx)} ${RadialMenu.numberToString(chevronDy)}`,
    );
    g.appendChild(chevron);
  } else if (centerImg) {
    var img = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    img.setAttribute('href', backButtonImg);
    img.setAttribute('height', '20');
    img.setAttribute('width', '40');
    img.setAttribute('x', -20);
    img.setAttribute('y', -10);

    // var use = self.createUseTag(0,0, icon);
    // use.setAttribute('width', size);
    // use.setAttribute('height', size);
    // img.setAttribute('transform', 'translate(-' + RadialMenu.numberToString(size / 2) + ',-' + RadialMenu.numberToString(size / 2) + ')');

    g.appendChild(img);
  }

  svg.appendChild(g);
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.getIndexOffset = function () {
  var self = this;
  if (self.levelItems.length < self.sectorCount) {
    switch (self.levelItems.length) {
      case 1:
        return -2;
      case 2:
        return -2;
      case 3:
        return -2;
      default:
        return -1;
    }
  } else {
    return -1;
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.createMenu = function (classValue, levelItems, nested) {
  var self = this;

  self.levelItems = levelItems;

  self.sectorCount = Math.max(self.levelItems.length, MIN_SECTORS);
  self.scale = self.calcScale();

  var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', classValue);
  // svg.setAttribute('viewBox', '-70 -45 140 100');

  if (window.matchMedia('(max-width: 500px)').matches) {
    svg.setAttribute('viewBox', '-26 -55 55 100'); // tablet
  } else if (window.matchMedia('(max-width: 800px)').matches) {
    svg.setAttribute('viewBox', '-55 -30 110 90'); // tablet
  } else if (window.matchMedia('(max-width: 1200px)').matches) {
    svg.setAttribute('viewBox', '-60 -30 120 85'); // tablet
  } else {
    svg.setAttribute('viewBox', '-70 -45 140 105'); // desktop
  }

  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  // svg.setAttribute('style', 'max-height: 550px');

  var angleStep = 360 / self.sectorCount;
  var angleShift = angleStep / 2 + 270;

  var indexOffset = self.getIndexOffset();

  self.depth += 1;
  for (var i = 0; i < self.sectorCount; ++i) {
    var startAngle = angleShift + angleStep * i;
    var endAngle = angleShift + angleStep * (i + 1);

    var itemIndex = RadialMenu.resolveLoopIndex(
      self.sectorCount - i + indexOffset,
      self.sectorCount,
    );
    var item;
    if (itemIndex >= 0 && itemIndex < self.levelItems.length) {
      item = self.levelItems[itemIndex];
    } else {
      item = null;
    }

    self.appendSectorPath(
      startAngle,
      endAngle,
      angleShift,
      svg,
      item,
      itemIndex,
    );
  }

  if (nested) {
    self.createCenter(svg, '', true, 8);
  } else {
    self.createCenter(svg, '', false, 7);
  }

  svg.addEventListener('mousedown', function (event) {
    var parentClass = event.target.parentNode.getAttribute('class');
    if (parentClass) {
      var className = parentClass.split(' ')[0];
      switch (className) {
        case 'sector':
          var index = parseInt(
            event.target.parentNode.getAttribute('data-index'),
          );
          if (!isNaN(index)) {
            self.setSelectedIndex(index);
          }
          break;
        case 'center':
          self.handleCenterClick();
          break;
        default:
      }
    }
  });

  return svg;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.selectDelta = function (indexDelta) {
  var self = this;
  var selectedIndex = self.getSelectedIndex();
  if (selectedIndex < 0) {
    selectedIndex = 0;
  }
  selectedIndex += indexDelta;

  if (selectedIndex < 0) {
    selectedIndex = self.levelItems.length + selectedIndex;
  } else if (selectedIndex >= self.levelItems.length) {
    selectedIndex -= self.levelItems.length;
  }
  self.setSelectedIndex(selectedIndex);
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.onKeyDown = function (event) {
  // var self = this;
  // if (self.currentMenu) {
  //     switch (event.key) {
  //         case 'Escape':
  //         case 'Backspace':
  //             self.handleCenterClick();
  //             event.preventDefault();
  //             break;
  //         case 'Enter':
  //             self.handleClick();
  //             event.preventDefault();
  //             break;
  //         case 'ArrowRight':
  //         case 'ArrowUp':
  //             self.selectDelta(1);
  //             event.preventDefault();
  //             break;
  //         case 'ArrowLeft':
  //         case 'ArrowDown':
  //             self.selectDelta(-1);
  //             event.preventDefault();
  //             break;
  //     }
  // }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.onMouseWheel = function (event) {
  var self = this;
  if (self.currentMenu) {
    var delta = -event.deltaY;

    if (delta > 0) {
      self.selectDelta(1);
    } else {
      self.selectDelta(-1);
    }
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.getSelectedNode = function () {
  var self = this;
  var items = self.currentMenu.getElementsByClassName('selected');
  if (items.length > 0) {
    return items[0];
  } else {
    return null;
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.getSelectedIndex = function () {
  var self = this;
  var selectedNode = self.getSelectedNode();
  if (selectedNode) {
    return parseInt(selectedNode.getAttribute('data-index'));
  } else {
    return -1;
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.setSelectedIndex = function (index) {
  var self = this;
  if (index >= 0 && index < self.levelItems.length) {
    var items = self.currentMenu.querySelectorAll(
      'g[data-index="' + index + '"]',
    );
    if (items.length > 0) {
      var itemToSelect = items[0];
      var selectedNode = self.getSelectedNode();

      if (selectedNode) {
        selectedNode.setAttribute('class', 'sector');
      }
      itemToSelect.setAttribute('class', 'sector selected');
    }
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.createUseTag = function (x, y, link) {
  var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttribute('x', RadialMenu.numberToString(x));
  use.setAttribute('y', RadialMenu.numberToString(y));
  use.setAttribute('width', '10');
  use.setAttribute('height', '10');
  use.setAttribute('fill', 'white');
  use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', link);
  return use;
};

RadialMenu.prototype.calculateTextPosition = function (
  title,
  startAngleDeg,
  endAngleDeg,
) {
  var middle = (endAngleDeg - startAngleDeg) / 2;
  var newAngle = startAngleDeg + middle;

  var cos = Math.cos(RadialMenu.degToRad(newAngle));
  var sin = Math.sin(RadialMenu.degToRad(newAngle));

  var total = (this.radius - this.innerRadius) / 2;
  var length = this.innerRadius + total;

  return {
    x: sin * length,
    y: cos * length,
  };
};

function isDoubleClick(event) {
  return event.detail && event.detail === 2;
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.appendSectorPath = function (
  startAngleDeg,
  endAngleDeg,
  angleShift,
  svg,
  item,
  index,
) {
  var self = this;

  var centerPoint = self.getSectorCenter(startAngleDeg, endAngleDeg);
  var translate = {
    x: (1 - self.scale) * centerPoint.x,
    y: (1 - self.scale) * centerPoint.y,
  };

  var g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  g.setAttribute('open', false);
  g.setAttribute(
    'transformAux',
    `translate(${translate.x},${translate.y}) scale(${self.scale + 0.05})`,
  );
  g.setAttribute(
    'transform',
    `translate(${translate.x},${translate.y}) scale(${self.scale + 0.05})`,
  );

  g.addEventListener('click', function (event) {
    var sectorClass = event.currentTarget.getAttribute('class');
    if (sectorClass !== 'dummy') {
      var nItem = {
        depth: Number(event.currentTarget.getAttribute('depth')),
        text: event.currentTarget.getAttribute('title'),
        id: Number(event.currentTarget.getAttribute('data-id')),
      };

      self.updateBreadcrumb(nItem);

      var newTranslate = {
        x: (1 - self.scale) * (centerPoint.x * 2),
        y: (1 - self.scale) * (centerPoint.y * 2),
      };

      var options =
        event.currentTarget.parentNode.getElementsByClassName('sector');
      var g = event.currentTarget;
      var open = event.currentTarget.getAttribute('open');

      if (isDoubleClick(event)) {
        self.handleClick(true);
        return;
      }

      if (open === 'true') {
        g.setAttribute('open', false);
        if (!item.items || item.items.length === 0) {
          g.setAttribute('transform', g.getAttribute('transformAux'));
        }
        if (self.theme === 'light') {
          var closeColor = self.getSectorColor(item.title);
          g.setAttribute('fill', closeColor);
          g.setAttribute('stroke', closeColor);
        } else {
          g.setAttribute('fill', self.tokens.baseFill);
        }

        self.handleClose();
      } else {
        // check if other options are opened and close them
        for (const option of options) {
          if (
            option.getAttribute('open') === 'true' &&
            option.dataset.id !== item.id
          ) {
            option.setAttribute('open', false);
            option.setAttribute(
              'transform',
              option.getAttribute('transformAux'),
            );
            if (self.theme === 'light') {
              var siblingColor = self.getSectorColor(
                option.getAttribute('title'),
              );
              option.setAttribute('fill', siblingColor);
              option.setAttribute('stroke', siblingColor);
            } else {
              option.setAttribute('fill', self.tokens.baseFill);
            }
            self.handleClose();
          }
        }

        if (self.theme !== 'light') {
          var gCenter =
            event.currentTarget.parentNode.getElementsByClassName(
              'center',
            )[0];

          var currentImageCenter = gCenter.querySelector('image');
          if (currentImageCenter) {
            gCenter.removeChild(currentImageCenter);
          }

          var image = event.currentTarget.getAttribute('image');
          if (image) {
            var img = document.createElementNS(
              'http://www.w3.org/2000/svg',
              'image',
            );
            img.setAttribute('href', image);
            img.setAttribute('height', '30');
            img.setAttribute('width', '30');
            img.setAttribute('x', 0);
            img.setAttribute('y', 0);
            img.setAttribute('transform', 'translate(-15,-15)');

            gCenter.appendChild(img);
          }
        }

        g.setAttribute('open', true);
        if (!item.items || item.items.length === 0) {
          g.setAttribute(
            'transform',
            `translate(${newTranslate.x},${newTranslate.y}) scale(${self.scale + 0.05})`,
          );
        }
        if (self.theme === 'light') {
          var openColor = self.getSectorColor(item.title);
          g.setAttribute('fill', openColor);
          g.setAttribute('stroke', openColor);
        } else {
          g.setAttribute('fill', self.tokens.selectedFill);
        }
        g.setAttribute('class', 'sector selected');
        self.handleClick();
      }
    }
  });

  g.addEventListener('mouseover', function (event) {
    if (self.theme === 'light') {
      return;
    }

    var open = event.currentTarget.getAttribute('open');

    var brothers = Array.from(svg.childNodes);
    var openedBrothers = brothers.filter(
      g => g.getAttribute('open') === 'true',
    );

    if (open === 'false' && openedBrothers.length === 0) {
      var gCenter = brothers.filter(
        g => g.getAttribute('class') === 'center',
      )[0];

      //removing the back button image
      var currentImageCenter = gCenter.querySelector('image');
      if (currentImageCenter) {
        gCenter.removeChild(currentImageCenter);
      }

      var image = event.currentTarget.getAttribute('image');
      if (image) {
        var img = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'image',
        );
        img.setAttribute('href', image);
        img.setAttribute('height', '30');
        img.setAttribute('width', '30');
        img.setAttribute('x', 0);
        img.setAttribute('y', 0);
        img.setAttribute('transform', 'translate(-15,-15)');

        gCenter.appendChild(img);
      }
    }
  });

  g.addEventListener('mouseout', function (event) {
    if (self.theme === 'light') {
      return;
    }

    var open = event.target.parentNode.getAttribute('open');
    // var openedBrothers = document.querySelectorAll('g[open="true"]')
    var brothers = Array.from(svg.childNodes);
    var openedBrothers = brothers.filter(
      g => g.getAttribute('open') === 'true',
    );
    if (open === 'false' && openedBrothers.length === 0) {
      var gCenter =
        event.target.parentNode.parentNode.getElementsByClassName('center')[0];

      var currentImageCenter = gCenter.querySelector('image');
      if (currentImageCenter) {
        gCenter.removeChild(currentImageCenter);
      }

      var img = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      img.setAttribute('href', backButtonImg);
      img.setAttribute('height', '25');
      img.setAttribute('width', '25');
      img.setAttribute('x', 0);
      img.setAttribute('y', 0);
      img.setAttribute('transform', 'translate(-12.5,-12.5)');

      gCenter.appendChild(img);
    }
  });

  var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', self.createSectorCmds(startAngleDeg, endAngleDeg));
  g.appendChild(path);

  if (item) {
    g.setAttribute('class', 'sector');
    g.setAttribute('depth', self.depth);
    g.setAttribute('data-id', item.id);
    g.setAttribute('title', item.title);
    g.setAttribute('data-index', index);

    var sectorColor =
      self.theme === 'light' ? self.getSectorColor(item.title) : null;
    if (self.theme === 'light') {
      g.setAttribute('fill', sectorColor);
      g.setAttribute('stroke', sectorColor);
    } else {
      g.setAttribute('fill', self.tokens.baseFill);
    }
    g.setAttribute('image', item.image);

    if (item.title) {
      var texts = self.createText(centerPoint.x, centerPoint.y, item.title);
      texts.forEach(text => {
        if (self.theme === 'light') {
          text.setAttribute('fill', sectorColor);
        }
        if (endAngleDeg - angleShift > 120 && endAngleDeg - angleShift < 300) {
          text.setAttribute('transform', 'translate(0,3)');
        }

        g.appendChild(text);
      });
    }

    if (item.icon) {
      var use = self.createUseTag(centerPoint.x, centerPoint.y, item.icon);
      if (self.theme === 'light') {
        use.setAttribute('fill', sectorColor);
      }
      if (item.title) {
        use.setAttribute('transform', 'translate(-5,-8)');
      } else {
        use.setAttribute('transform', 'translate(-5,-5)');
      }

      g.appendChild(use);
    }
  } else {
    g.setAttribute('class', 'dummy');
    if (self.theme === 'light') {
      g.setAttribute('fill', LIGHT_DEFAULT_SECTOR_COLOR);
      g.setAttribute('stroke', LIGHT_DEFAULT_SECTOR_COLOR);
    } else {
      g.setAttribute('fill', self.tokens.dummyFill);
    }
  }

  svg.appendChild(g);
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.createSectorCmds = function (startAngleDeg, endAngleDeg) {
  var self = this;

  var initPoint = RadialMenu.getDegreePos(startAngleDeg, self.radius);
  var path = 'M' + RadialMenu.pointToString(initPoint);

  var radiusAfterScale = self.radius * (1 / self.scale);
  path +=
    'A' +
    radiusAfterScale +
    ' ' +
    radiusAfterScale +
    ' 0 0 0' +
    RadialMenu.pointToString(RadialMenu.getDegreePos(endAngleDeg, self.radius));
  path +=
    'L' +
    RadialMenu.pointToString(
      RadialMenu.getDegreePos(endAngleDeg, self.innerRadius),
    );

  var radiusDiff = self.radius - self.innerRadius;
  var radiusDelta = (radiusDiff - radiusDiff * self.scale) / 2;
  var innerRadius = (self.innerRadius + radiusDelta) * (1 / self.scale);
  path +=
    'A' +
    innerRadius +
    ' ' +
    innerRadius +
    ' 0 0 1 ' +
    RadialMenu.pointToString(
      RadialMenu.getDegreePos(startAngleDeg, self.innerRadius),
    );
  path += 'Z';

  return path;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.createText = function (x, y, title) {
  var self = this;

  var axisY = y - 3;
  var words = title.split(' ');
  var textList = [];

  for (const word of words) {
    var text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('font-size', '3.5px');
    text.setAttribute('x', x);
    text.setAttribute('y', words.length > 1 ? axisY : y);
    text.innerHTML = word;
    axisY += 5;
    textList.push(text);
  }

  return textList;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.createCircle = function (x, y, r) {
  var circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('cx', RadialMenu.numberToString(x));
  circle.setAttribute('cy', RadialMenu.numberToString(y));
  circle.setAttribute('r', r);
  return circle;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.calcScale = function () {
  var self = this;
  var totalSpace = self.sectorSpace * self.sectorCount;
  var circleLength = Math.PI * 2 * self.radius;
  var radiusDelta = self.radius - (circleLength - totalSpace) / (Math.PI * 2);
  return (self.radius - radiusDelta) / self.radius;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.getSectorCenter = function (startAngleDeg, endAngleDeg) {
  var self = this;
  var positions = RadialMenu.getDegreePos(
    (startAngleDeg + endAngleDeg) / 2,
    self.innerRadius + (self.radius - self.innerRadius) / 2,
  );

  return positions;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.prototype.addIconSymbols = function () {
  var self = this;
  var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'icons');

  // return
  var returnSymbol = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'symbol',
  );
  returnSymbol.setAttribute('id', 'return');
  returnSymbol.setAttribute('viewBox', '0 0 489.394 489.394');
  var returnPath = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'path',
  );
  returnPath.setAttribute(
    'd',
    'M375.789,92.867H166.864l17.507-42.795c3.724-9.132,1-19.574-6.691-25.744c-7.701-6.166-18.538-6.508-26.639-0.879' +
      'L9.574,121.71c-6.197,4.304-9.795,11.457-9.563,18.995c0.231,7.533,4.261,14.446,10.71,18.359l147.925,89.823' +
      'c8.417,5.108,19.18,4.093,26.481-2.499c7.312-6.591,9.427-17.312,5.219-26.202l-19.443-41.132h204.886' +
      'c15.119,0,27.418,12.536,27.418,27.654v149.852c0,15.118-12.299,27.19-27.418,27.19h-226.74c-20.226,0-36.623,16.396-36.623,36.622' +
      'v12.942c0,20.228,16.397,36.624,36.623,36.624h226.74c62.642,0,113.604-50.732,113.604-113.379V206.709' +
      'C489.395,144.062,438.431,92.867,375.789,92.867z',
  );
  returnSymbol.appendChild(returnPath);
  svg.appendChild(returnSymbol);

  var closeSymbol = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'symbol',
  );
  closeSymbol.setAttribute('id', 'close');
  closeSymbol.setAttribute('viewBox', '0 0 41.756 41.756');

  var closePath = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'path',
  );
  closePath.setAttribute(
    'd',
    'M27.948,20.878L40.291,8.536c1.953-1.953,1.953-5.119,0-7.071c-1.951-1.952-5.119-1.952-7.07,0L20.878,13.809L8.535,1.465' +
      'c-1.951-1.952-5.119-1.952-7.07,0c-1.953,1.953-1.953,5.119,0,7.071l12.342,12.342L1.465,33.22c-1.953,1.953-1.953,5.119,0,7.071' +
      'C2.44,41.268,3.721,41.755,5,41.755c1.278,0,2.56-0.487,3.535-1.464l12.343-12.342l12.343,12.343' +
      'c0.976,0.977,2.256,1.464,3.535,1.464s2.56-0.487,3.535-1.464c1.953-1.953,1.953-5.119,0-7.071L27.948,20.878z',
  );
  closeSymbol.appendChild(closePath);
  svg.appendChild(closeSymbol);

  self.holder.appendChild(svg);
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.getDegreePos = function (angleDeg, length) {
  return {
    x: Math.sin(RadialMenu.degToRad(angleDeg)) * length,
    y: Math.cos(RadialMenu.degToRad(angleDeg)) * length,
  };
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.pointToString = function (point) {
  return (
    RadialMenu.numberToString(point.x) +
    ' ' +
    RadialMenu.numberToString(point.y)
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.numberToString = function (n) {
  if (Number.isInteger(n)) {
    return n.toString();
  } else if (n) {
    var r = (+n).toFixed(5);
    if (r.match(/\./)) {
      r = r.replace(/\.?0+$/, '');
    }
    return r;
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.resolveLoopIndex = function (index, length) {
  if (index < 0) {
    index = length + index;
  }
  if (index >= length) {
    index = index - length;
  }
  if (index < length) {
    return index;
  } else {
    return null;
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.degToRad = function (deg) {
  return deg * (Math.PI / 180);
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.setClassAndWaitForTransition = function (node, newClass) {
  return new Promise(function (resolve) {
    function handler(event) {
      if (event.target == node && event.propertyName == 'visibility') {
        node.removeEventListener('transitionend', handler);
        resolve();
      }
    }
    node.addEventListener('transitionend', handler);
    node.setAttribute('class', newClass);
  });
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
RadialMenu.nextTick = function (fn) {
  setTimeout(fn, 10);
};
